// frontend/src/pages/MovimentacaoDepositos.js
import React, { useState, useEffect } from 'react';
import { ArrowRightLeft, Search, Package, CheckCircle, AlertCircle, Loader2, Info } from 'lucide-react';

const MovimentacaoDepositos = ({ apiFetch, formatCurrency }) => {
    const [deposits, setDeposits] = useState([]);
    
    // Estados da Busca
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    
    // Estado do Produto Selecionado
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [productDetails, setProductDetails] = useState(null);
    const [isLoadingDetails, setIsLoadingDetails] = useState(false);

    // Estados do Formulário
    const [sourceDepositId, setSourceDepositId] = useState('');
    const [targetDepositId, setTargetDepositId] = useState('');
    const [quantity, setQuantity] = useState('');
    const [packagingName, setPackagingName] = useState('');
    const [observations, setObservations] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    // Busca os depósitos ao carregar a página
    useEffect(() => {
        const fetchDeposits = async () => {
            try {
                const data = await apiFetch('/deposits');
                setDeposits(data.filter(d => d.status === 'ativo'));
            } catch (err) {
                console.error("Erro ao buscar depósitos:", err);
            }
        };
        fetchDeposits();
    }, [apiFetch]);

    // Lógica de Busca de Produto (Autocomplete)
    useEffect(() => {
        const delayDebounceFn = setTimeout(async () => {
            if (searchQuery.length >= 2) {
                setIsSearching(true);
                try {
                    const results = await apiFetch(`/stock/search?q=${encodeURIComponent(searchQuery)}`);
                    setSearchResults(results);
                } catch (err) {
                    console.error("Erro na busca:", err);
                } finally {
                    setIsSearching(false);
                }
            } else {
                setSearchResults([]);
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchQuery, apiFetch]);

    // Ao selecionar um produto na busca
    const handleSelectProduct = async (product) => {
        setSelectedProduct(product);
        setSearchQuery('');
        setSearchResults([]);
        setIsLoadingDetails(true);
        setErrorMessage('');
        setSuccessMessage('');

        try {
            // Busca os detalhes completos para saber o saldo em cada depósito
            const details = await apiFetch(`/stock/product/${product.id}`);
            setProductDetails(details);
            
            // Reseta o formulário
            setSourceDepositId('');
            setTargetDepositId('');
            setQuantity('');
            setPackagingName('');
            setObservations('');
        } catch (err) {
            setErrorMessage("Erro ao carregar detalhes do produto.");
        } finally {
            setIsLoadingDetails(false);
        }
    };

    // Enviar a Transferência
    const handleTransfer = async (e) => {
        e.preventDefault();
        setErrorMessage('');
        setSuccessMessage('');

        if (!selectedProduct || !sourceDepositId || !targetDepositId || !quantity) {
            setErrorMessage("Preencha todos os campos obrigatórios.");
            return;
        }

        if (sourceDepositId === targetDepositId) {
            setErrorMessage("O depósito de origem e destino não podem ser o mesmo.");
            return;
        }

        const qtyNum = parseFloat(quantity.replace(',', '.'));
        if (isNaN(qtyNum) || qtyNum <= 0) {
            setErrorMessage("Informe uma quantidade válida maior que zero.");
            return;
        }

        // Validação visual de saldo (o backend também valida, mas aqui evita requisição desnecessária)
        const sourceBalance = productDetails?.depositos?.find(d => d.id === parseInt(sourceDepositId))?.quantidade || 0;
        if (sourceBalance < qtyNum) {
            setErrorMessage(`Saldo insuficiente na origem. Disponível: ${sourceBalance}`);
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                variation_id: selectedProduct.id,
                source_deposit_id: parseInt(sourceDepositId),
                target_deposit_id: parseInt(targetDepositId),
                quantity: qtyNum,
                packaging_name: packagingName,
                observations: observations
            };

            await apiFetch('/stock/transfer', {
                method: 'POST',
                body: payload
            });

            setSuccessMessage('Transferência realizada com sucesso! O histórico foi atualizado.');
            
            // Atualiza os saldos na tela buscando os detalhes do produto novamente
            const updatedDetails = await apiFetch(`/stock/product/${selectedProduct.id}`);
            setProductDetails(updatedDetails);
            
            // Limpa formulário
            setQuantity('');
            setPackagingName('');
            setObservations('');
            
        } catch (err) {
            setErrorMessage(err.message || "Erro ao realizar transferência.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="p-4 md:p-8 max-w-6xl mx-auto pb-24">
            <div className="flex items-center gap-3 mb-6 border-b pb-4">
                <div className="p-3 bg-indigo-100 text-indigo-600 rounded-lg">
                    <ArrowRightLeft size={28} />
                </div>
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Movimentação de Depósitos</h1>
                    <p className="text-sm text-gray-500">Transfira produtos entre os locais de estoque da sua entidade.</p>
                </div>
            </div>

            {/* --- BLOCO 1: BUSCA DE PRODUTO --- */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 mb-6 relative">
                <label className="block text-sm font-semibold text-gray-700 mb-2">1. Selecione o Produto para Transferência</label>
                <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search size={18} className="text-gray-400" />
                    </div>
                    <input
                        type="text"
                        placeholder="Digite o nome, código ou EAN do produto..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-gray-700"
                    />
                    {isSearching && (
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                            <Loader2 size={18} className="animate-spin text-indigo-500" />
                        </div>
                    )}
                </div>

                {/* Resultados da Busca */}
                {searchResults.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                        {searchResults.map((prod) => (
                            <div 
                                key={prod.id} 
                                onClick={() => handleSelectProduct(prod)}
                                className="p-3 border-b border-gray-100 hover:bg-indigo-50 cursor-pointer flex justify-between items-center transition-colors"
                            >
                                <div>
                                    <p className="font-semibold text-gray-800 text-sm">{prod.name}</p>
                                    <p className="text-xs text-gray-500 mt-0.5">Cód: {prod.code || '-'} | EAN: {prod.ean || '-'}</p>
                                </div>
                                <div className="text-right">
                                    <span className="text-xs font-bold text-indigo-600 bg-indigo-100 px-2 py-1 rounded-full">
                                        Estoque Total: {prod.total_stock}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* --- BLOCO 2: DETALHES E FORMULÁRIO --- */}
            {isLoadingDetails ? (
                <div className="flex justify-center p-12">
                    <Loader2 size={32} className="animate-spin text-indigo-600" />
                </div>
            ) : selectedProduct && productDetails ? (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    
                    {/* Coluna Esquerda: Informações do Produto */}
                    <div className="lg:col-span-1 space-y-4">
                        <div className="bg-indigo-50 border border-indigo-100 p-5 rounded-xl">
                            <div className="flex items-center gap-2 mb-3 text-indigo-800">
                                <Package size={20} />
                                <h2 className="font-bold text-lg leading-tight">{productDetails.descricao}</h2>
                            </div>
                            <div className="text-sm text-indigo-900/80 space-y-1 mb-4">
                                <p><strong>Código:</strong> {productDetails.codigo || 'N/A'}</p>
                                <p><strong>EAN:</strong> {productDetails.ean || 'N/A'}</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg border border-indigo-100 text-center">
                                <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Estoque Total</p>
                                <p className="text-3xl font-black text-indigo-600">{productDetails.estoque_total}</p>
                            </div>
                        </div>

                        <div className="bg-white border border-gray-200 p-5 rounded-xl shadow-sm">
                            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                                <Info size={16} className="text-gray-400"/> Saldos por Depósito
                            </h3>
                            <div className="space-y-2">
                                {productDetails.depositos.map(dep => (
                                    <div key={dep.id} className="flex justify-between items-center p-2 bg-gray-50 rounded-md border border-gray-100">
                                        <span className="text-sm font-medium text-gray-700">{dep.nome}</span>
                                        <span className={`text-sm font-bold px-2 py-0.5 rounded ${dep.quantidade > 0 ? 'bg-green-100 text-green-700' : 'bg-red-50 text-red-500'}`}>
                                            {dep.quantidade}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Coluna Direita: Formulário de Transferência */}
                    <div className="lg:col-span-2">
                        <form onSubmit={handleTransfer} className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-200">
                            <h2 className="text-xl font-bold text-gray-800 mb-6 border-b pb-2">2. Dados da Transferência</h2>
                            
                            {errorMessage && (
                                <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 flex items-center gap-3 rounded-r-lg">
                                    <AlertCircle size={20} className="shrink-0" />
                                    <p className="text-sm font-medium">{errorMessage}</p>
                                </div>
                            )}

                            {successMessage && (
                                <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 text-green-700 flex items-center gap-3 rounded-r-lg">
                                    <CheckCircle size={20} className="shrink-0" />
                                    <p className="text-sm font-medium">{successMessage}</p>
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Depósito de Origem (Sai de)</label>
                                    <select 
                                        required
                                        value={sourceDepositId} 
                                        onChange={e => setSourceDepositId(e.target.value)}
                                        className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                                    >
                                        <option value="">Selecione a origem...</option>
                                        {/* Filtra apenas os depósitos que possuem saldo positivo deste produto */}
                                        {productDetails.depositos.filter(d => d.quantidade > 0).map(dep => (
                                            <option key={dep.id} value={dep.id}>{dep.nome} (Saldo: {dep.quantidade})</option>
                                        ))}
                                    </select>
                                    {productDetails.depositos.filter(d => d.quantidade > 0).length === 0 && (
                                        <p className="text-xs text-red-500 mt-1 flex items-center gap-1"><AlertCircle size={12}/> Não há saldo em nenhum depósito para transferir.</p>
                                    )}
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Depósito de Destino (Entra em)</label>
                                    <select 
                                        required
                                        value={targetDepositId} 
                                        onChange={e => setTargetDepositId(e.target.value)}
                                        disabled={!sourceDepositId}
                                        className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                                    >
                                        <option value="">Selecione o destino...</option>
                                        {deposits.filter(d => d.id !== parseInt(sourceDepositId)).map(dep => (
                                            <option key={dep.id} value={dep.id}>{dep.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Quantidade a Transferir</label>
                                    <input 
                                        type="number" 
                                        step="0.01"
                                        required
                                        placeholder="Ex: 10"
                                        value={quantity}
                                        onChange={e => setQuantity(e.target.value)}
                                        className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Embalagem <span className="text-gray-400 font-normal">(Opcional)</span></label>
                                    <input 
                                        type="text" 
                                        placeholder="Ex: Caixa, Unidade, KG"
                                        value={packagingName}
                                        onChange={e => setPackagingName(e.target.value)}
                                        className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                                    />
                                </div>
                            </div>

                            <div className="mb-8">
                                <label className="block text-sm font-bold text-gray-700 mb-1">Observações / Motivo <span className="text-gray-400 font-normal">(Opcional)</span></label>
                                <textarea 
                                    rows="2"
                                    placeholder="Ex: Transferência para abastecer loja da frente..."
                                    value={observations}
                                    onChange={e => setObservations(e.target.value)}
                                    className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none resize-none"
                                ></textarea>
                            </div>

                            <div className="flex justify-end pt-4 border-t border-gray-100">
                                <button 
                                    type="submit" 
                                    disabled={isSubmitting || !sourceDepositId || !targetDepositId || !quantity}
                                    className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg shadow-md transition-colors flex items-center justify-center gap-2 disabled:bg-gray-400 disabled:cursor-not-allowed min-w-[200px]"
                                >
                                    {isSubmitting ? (
                                        <><Loader2 size={20} className="animate-spin"/> Processando...</>
                                    ) : (
                                        <><ArrowRightLeft size={20}/> Confirmar Transferência</>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>

                </div>
            ) : null}

        </div>
    );
};

export default MovimentacaoDepositos;